<template>
  <div class="split-view-container" :class="{ 'is-dragging': isDragging }" :style="{ height: containerHeight }">
    <template v-for="(width, index) in panelWidths" :key="'p-' + index">
      <div
        class="split-panel"
        :style="{ width: width + 'px', left: panelOffsets[index] + 'px' }"
      >
        <slot :name="'panel-' + index"></slot>
      </div>

      <vue-draggable-resizable
        v-if="index < panelCount - 1"
        class="split-divider"
        :w="dividerWidth"
        :h="containerHeightNumber"
        :x="panelOffsets[index] + width"
        :y="0"
        :min-width="dividerWidth"
        :max-width="dividerWidth"
        :resizable="false"
        :draggable="true"
        :parent="true"
        :handles="[]"
        axis="x"
        @drag-start="isDragging = true"
        @drag-stop="isDragging = false"
        @drag="(x, y) => onDividerDrag(index, x)"
      >
        <div class="divider-handle">
          <div class="divider-line"></div>
        </div>
      </vue-draggable-resizable>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SplitView',
  props: {
    panelCount: {
      type: Number,
      default: 2
    },
    initialWidths: {
      type: Array,
      default: null
    },
    height: {
      type: String,
      default: '400px'
    },
    minPanelWidth: {
      type: Number,
      default: 100
    }
  },
  data() {
    return {
      containerWidth: 0,
      panelWidths: [],
      dividerWidth: 8,
      isDragging: false,
    };
  },
  computed: {
    containerHeight() {
      return this.height;
    },
    containerHeightNumber() {
      const match = this.height.match(/(\d+)/);
      return match ? parseInt(match[1]) : 400;
    },
    panelOffsets() {
      const offsets = [];
      let offset = 0;
      for (let i = 0; i < this.panelWidths.length; i++) {
        offsets.push(offset);
        offset += this.panelWidths[i] + this.dividerWidth;
      }
      return offsets;
    }
  },
  mounted() {
    this.initializeWidths();
    this.setupResizeListener();
  },
  beforeUnmount() {
    this.removeResizeListener();
  },
  methods: {
    initializeWidths() {
      this.$nextTick(() => {
        const container = this.$el;
        if (container) {
          this.containerWidth = container.clientWidth;
          this.computePanelWidths();
        }
      });
    },
    computePanelWidths() {
      const totalDividerWidth = (this.panelCount - 1) * this.dividerWidth;
      const availableWidth = this.containerWidth - totalDividerWidth;

      let widths;
      if (this.initialWidths && this.initialWidths.length === this.panelCount) {
        widths = this.initialWidths.map(pct => Math.floor(availableWidth * (pct / 100)));
      } else {
        const equalWidth = Math.floor(availableWidth / this.panelCount);
        widths = Array(this.panelCount).fill(equalWidth);
      }

      const total = widths.reduce((a, b) => a + b, 0);
      widths[this.panelCount - 1] += availableWidth - total;
      this.panelWidths = widths;
      this.enforceMinWidths();
    },
    setupResizeListener() {
      this.resizeHandler = () => this.handleWindowResize();
      window.addEventListener('resize', this.resizeHandler);
    },
    removeResizeListener() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
    },
    handleWindowResize() {
      const container = this.$el;
      if (container) {
        const newContainerWidth = container.clientWidth;
        const oldAvailable = this.containerWidth - (this.panelCount - 1) * this.dividerWidth;
        const newAvailable = newContainerWidth - (this.panelCount - 1) * this.dividerWidth;
        const ratio = oldAvailable > 0 ? newAvailable / oldAvailable : 1;

        this.containerWidth = newContainerWidth;
        const newWidths = this.panelWidths.map(w => Math.floor(w * ratio));
        const total = newWidths.reduce((a, b) => a + b, 0);
        newWidths[this.panelCount - 1] += newAvailable - total;
        this.panelWidths = newWidths;
        this.enforceMinWidths();
      }
    },
    onDividerDrag(dividerIndex, x) {
      const panelI = dividerIndex;
      const panelJ = dividerIndex + 1;
      const offsetI = this.panelOffsets[panelI];
      const combinedWidth = this.panelWidths[panelI] + this.dividerWidth + this.panelWidths[panelJ];

      let newWidthI = x - offsetI;
      newWidthI = Math.max(
        this.minPanelWidth,
        Math.min(newWidthI, combinedWidth - this.dividerWidth - this.minPanelWidth)
      );
      const newWidthJ = combinedWidth - this.dividerWidth - newWidthI;

      const newWidths = [...this.panelWidths];
      newWidths[panelI] = newWidthI;
      newWidths[panelJ] = newWidthJ;
      this.panelWidths = newWidths;

      this.$emit('resize', {
        panelWidths: [...this.panelWidths],
        panelPercentages: this.panelWidths.map(w => {
          const totalDividerWidth = (this.panelCount - 1) * this.dividerWidth;
          return (w / (this.containerWidth - totalDividerWidth)) * 100;
        })
      });
    },
    enforceMinWidths() {
      this.panelWidths = this.panelWidths.map(w => Math.max(w, this.minPanelWidth));
    },
    setPanelWidths(percentages) {
      const totalDividerWidth = (this.panelCount - 1) * this.dividerWidth;
      const availableWidth = this.containerWidth - totalDividerWidth;
      const newWidths = percentages.map(pct => Math.floor(availableWidth * (pct / 100)));
      const total = newWidths.reduce((a, b) => a + b, 0);
      newWidths[newWidths.length - 1] += availableWidth - total;
      this.panelWidths = newWidths;
      this.enforceMinWidths();
    }
  }
};
</script>

<style scoped>
.split-view-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: #ffffff;
}

.split-view-container.is-dragging,
.split-view-container.is-dragging * {
  user-select: none;
  -webkit-user-select: none;
}

.split-panel {
  position: absolute;
  top: 0;
  height: 100%;
  overflow: auto;
  background-color: #ffffff;
}

.split-divider {
  position: absolute !important;
  top: 0;
  cursor: col-resize;
  background-color: #f3f4f6;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  z-index: 10;
  width: 10px !important;
  height: 100% !important;
  transition: background-color 0.2s ease;
}

.split-divider:hover {
  background-color: gray;
}

.split-divider:hover .divider-line {
  background-color: white;
}

.divider-handle {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.divider-line {
  width: 2px;
  height: 40px;
  background-color: gray;
  border-radius: 1px;
  position: relative;
}

@media (max-width: 768px) {
  .split-view-container {
    flex-direction: column;
  }

  .split-panel {
    position: relative;
    width: 100% !important;
    left: 0 !important;
    height: auto;
    min-height: 200px;
  }

  .split-divider {
    display: none;
  }
}

.split-panel::-webkit-scrollbar {
  width: 6px;
}

.split-panel::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.split-panel::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.split-panel::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
